import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, Scale, Cookie, ChevronRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const TABS = [
  {
    id: "privacy-policy",
    label: "Privacy Policy",
    icon: ShieldCheck,
    path: "/privacy-policy",
    heading: "Privacy Policy",
    subheading:
      "How Simpel collects, processes, and protects personal and enterprise data across our platforms.",
  },
  {
    id: "terms-of-use",
    label: "Terms of Use",
    icon: Scale,
    path: "/terms-of-use",
    heading: "Terms of Use",
    subheading:
      "General terms governing your access to Simpel's websites, demonstrations, and software services.",
  },
  {
    id: "cookie-policy",
    label: "Cookie Policy",
    icon: Cookie,
    path: "/cookie-policy",
    heading: "Cookie Policy",
    subheading:
      "Information regarding how we use essential and analytical cookies to ensure optimal performance.",
  },
];

const PRIVACY_SECTIONS = [
  { id: "priv-intro", title: "1. Overview & Scope" },
  { id: "priv-collect", title: "2. Information We Collect" },
  { id: "priv-use", title: "3. How We Use Information" },
  { id: "priv-ai", title: "4. AI Data Governance & Privacy" },
  { id: "priv-security", title: "5. Security & Encryption" },
  { id: "priv-sharing", title: "6. Data Sharing & Third Parties" },
  { id: "priv-rights", title: "7. Your Rights & Choices" },
  { id: "priv-contact", title: "8. Contact & Legal Requests" },
];

const TERMS_SECTIONS = [
  { id: "terms-acceptance", title: "1. Acceptance of Terms" },
  { id: "terms-ip", title: "2. Intellectual Property" },
  { id: "terms-use", title: "3. Permitted & Restricted Use" },
  { id: "terms-ai", title: "4. AI Output & Advisory Scope" },
  { id: "terms-liability", title: "5. Limitation of Liability" },
  { id: "terms-governing", title: "6. Governing Law & Jurisdiction" },
];

const COOKIE_SECTIONS = [
  { id: "cookie-intro", title: "1. What Are Cookies?" },
  { id: "cookie-types", title: "2. Categories of Cookies Used" },
  { id: "cookie-no-ads", title: "3. No Third-Party Ad Trackers" },
  { id: "cookie-manage", title: "4. Managing Cookie Preferences" },
  { id: "cookie-updates", title: "5. Policy Modifications" },
];

export const LegalPage = ({ initialTab }) => {
  const location = useLocation();

  const getTabFromPath = () => {
    const path = location.pathname.toLowerCase();
    if (path.includes("terms")) return "terms-of-use";
    if (path.includes("cookie")) return "cookie-policy";
    return "privacy-policy";
  };

  const [activeTab, setActiveTab] = useState(initialTab || getTabFromPath());
  const [activeSection, setActiveSection] = useState("");

  const currentTabInfo = TABS.find((t) => t.id === activeTab) || TABS[0];

  useEffect(() => {
    const matched = initialTab || getTabFromPath();
    setActiveTab(matched);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname, initialTab]);

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const navOffset = 90;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  // Sections for currently active tab
  const activeSections =
    activeTab === "privacy-policy"
      ? PRIVACY_SECTIONS
      : activeTab === "terms-of-use"
      ? TERMS_SECTIONS
      : COOKIE_SECTIONS;

  return (
    <div className="min-h-screen w-full bg-[#030914] text-[#E2E8F0] flex flex-col font-sans antialiased selection:bg-white selection:text-black">
      <Navbar />

      <main className="flex-1 pt-[76px] sm:pt-[80px]">
        {/* Editorial Header with Corporate Boardroom Background */}
        <section className="relative overflow-hidden min-h-[420px] sm:min-h-[460px] lg:min-h-[490px] flex items-center py-16 sm:py-20 lg:py-24 border-b border-white/[0.08]">
          {/* User-supplied background image */}
          <div className="absolute inset-0 z-0 select-none pointer-events-none">
            <img
              src="/images/legal/legal-hero-bg.jpeg"
              alt="Corporate Legal & Governance"
              className="w-full h-full object-cover filter brightness-[1.08] contrast-[1.05]"
              style={{ objectPosition: "center 8%" }}
            />
            {/* Soft dark tint with transparent right side allowing the image to be clearly visible */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(90deg, rgba(3, 9, 20, 0.88) 0%, rgba(3, 9, 20, 0.7) 35%, rgba(3, 9, 20, 0.35) 70%, rgba(3, 9, 20, 0.15) 100%)",
              }}
            />
            {/* Soft vertical blend */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(180deg, rgba(3, 9, 20, 0.35) 0%, transparent 35%, rgba(3, 9, 20, 0.88) 100%)",
              }}
            />
          </div>

          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            <div className="max-w-[720px] flex flex-col items-start text-left">
              {/* Clean corporate breadcrumb */}
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[2px] text-[#94A3B8]">
                <span>Simpel Governance</span>
                <span className="text-white/30">/</span>
                <span className="text-white">{currentTabInfo.label}</span>
              </div>

              {/* Dynamic headline matching current policy */}
              <h1 className="mt-4 text-[34px] sm:text-[44px] lg:text-[48px] font-bold tracking-tight text-white leading-[1.12]">
                {currentTabInfo.heading}
              </h1>

              {/* Description */}
              <p className="mt-4 text-[15px] sm:text-[16px] leading-[26px] text-[#A0AEC0] max-w-[660px]">
                {currentTabInfo.subheading}
              </p>
            </div>
          </div>
        </section>

        {/* Documentation Content Grid with Sticky Table of Contents */}
        <section className="mx-auto max-w-[1440px] px-6 lg:px-[70px] py-14 sm:py-18">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            
            {/* Left Sticky Table of Contents (Desktop) */}
            <aside className="hidden lg:block lg:col-span-3 sticky top-[105px]">
              <div className="rounded-[14px] bg-[#070F1E] border border-white/[0.08] p-5 shadow-lg shadow-black/20">
                <p className="text-[11px] font-bold uppercase tracking-[1.8px] text-[#718096] mb-3">
                  Contents
                </p>
                <nav className="flex flex-col space-y-1">
                  {activeSections.map((sec) => (
                    <button
                      key={sec.id}
                      onClick={() => scrollToSection(sec.id)}
                      className={`text-left text-[13px] py-1.5 px-2.5 rounded-[8px] transition-all cursor-pointer ${
                        activeSection === sec.id
                          ? "bg-white/10 text-white font-medium"
                          : "text-[#8A99AD] hover:text-white hover:bg-white/[0.04]"
                      }`}
                    >
                      {sec.title}
                    </button>
                  ))}
                </nav>

                <div className="mt-5 pt-4 border-t border-white/[0.08]">
                  <p className="text-[12px] text-[#718096]">
                    Questions or compliance requests?
                  </p>
                  <a
                    href="mailto:contact@simpel.ai"
                    className="inline-flex items-center gap-1 text-[12.5px] text-white hover:underline font-medium mt-1"
                  >
                    contact@simpel.ai <ChevronRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </aside>

            {/* Main Legal Document Content */}
            <article className="lg:col-span-9 max-w-[860px]">
              <div className="rounded-[16px] bg-[#070F1E]/60 border border-white/[0.08] p-6 sm:p-10 lg:p-12 shadow-xl shadow-black/25">
                {activeTab === "privacy-policy" && <PrivacyPolicyContent />}
                {activeTab === "terms-of-use" && <TermsOfUseContent />}
                {activeTab === "cookie-policy" && <CookiePolicyContent />}
              </div>
            </article>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

/* --- 1. PRIVACY POLICY DOCUMENT --- */
const PrivacyPolicyContent = () => (
  <div className="flex flex-col space-y-12 text-[#94A3B8] text-[15px] leading-[1.8]">
    {/* Notice box */}
    <div className="rounded-[12px] bg-[#0A1426] border border-white/10 p-5 text-[14px] text-[#CBD5E1]">
      <p className="leading-[22px]">
        This Privacy Policy details the data protection commitments of{" "}
        <strong className="text-white">Simpel Technologies</strong>. We adhere to
        recognized global standards including GDPR, SOC 2 principles, and applicable
        enterprise privacy frameworks.
      </p>
    </div>

    {/* Section 1 */}
    <section id="priv-intro" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        1. Overview & Scope
      </h2>
      <p className="mt-4">
        Simpel Technologies ("Simpel", "we", "us", or "our") is dedicated to protecting the
        confidentiality, integrity, and privacy of information entrusted to us by clients,
        partners, website visitors, and prospective team members.
      </p>
      <p className="mt-3">
        This policy applies to data collected through our public website (
        <span className="text-white font-medium">simpel.ai</span>), enterprise demonstrations,
        automation platform modules, and interactive sessions conducted via our conversational AI agent (Simmi).
      </p>
    </section>

    {/* Section 2 */}
    <section id="priv-collect" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        2. Information We Collect
      </h2>
      <p className="mt-4">
        We collect only the minimum necessary data to provide our automation architecture
        and business advisory services:
      </p>
      <ul className="mt-4 space-y-3.5 list-none pl-0">
        <li className="flex items-start gap-3">
          <span className="w-1.5 h-1.5 rounded-full bg-[#CBD5E1] mt-2.5 shrink-0" />
          <div>
            <strong className="text-white font-medium">Direct Business Inquiries:</strong>{" "}
            Name, work email, organization name, phone number, and project details submitted
            via contact requests, meeting bookings, or partnership proposals.
          </div>
        </li>
        <li className="flex items-start gap-3">
          <span className="w-1.5 h-1.5 rounded-full bg-[#CBD5E1] mt-2.5 shrink-0" />
          <div>
            <strong className="text-white font-medium">Employment Submissions:</strong>{" "}
            Professional resumes, employment history, portfolios, and qualifications submitted
            through our career portal.
          </div>
        </li>
        <li className="flex items-start gap-3">
          <span className="w-1.5 h-1.5 rounded-full bg-[#CBD5E1] mt-2.5 shrink-0" />
          <div>
            <strong className="text-white font-medium">System Telemetry & Interaction Logs:</strong>{" "}
            Browser type, operating system, session timestamps, and feature interactions used
            strictly to evaluate platform latency, uptime, and performance.
          </div>
        </li>
      </ul>
    </section>

    {/* Section 3 */}
    <section id="priv-use" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        3. How We Use Information
      </h2>
      <p className="mt-4">Information gathered by Simpel is used solely for the following legitimate purposes:</p>
      <ul className="mt-3 space-y-2 list-disc pl-5">
        <li>Delivering tailored enterprise automation workflows and proofs-of-concept.</li>
        <li>Coordinating consultation calls and executive strategy demonstrations.</li>
        <li>Conducting recruitment and candidate evaluations for engineering and operations roles.</li>
        <li>Protecting our digital infrastructure against fraudulent activity, cyber threats, and misuse.</li>
        <li>Maintaining audit logs required under applicable corporate and statutory standards.</li>
      </ul>
    </section>

    {/* Section 4 */}
    <section id="priv-ai" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        4. AI Data Governance & Privacy
      </h2>
      <p className="mt-4">
        As an enterprise AI technology company, Simpel maintains strict organizational boundaries
        regarding client data and artificial intelligence models:
      </p>
      <div className="mt-4 rounded-[10px] bg-[#0B1527] border border-white/10 p-5">
        <h3 className="text-white font-semibold text-[15px]">Zero Customer Model Training Policy</h3>
        <p className="mt-2 text-[14px] text-[#A0AEC0] leading-[22px]">
          Simpel does <strong>not</strong> train public foundation models or share proprietary
          client prompts, industrial parameters, or internal documents with third-party model
          providers without explicit, written contractual authorization.
        </p>
      </div>
    </section>

    {/* Section 5 */}
    <section id="priv-security" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        5. Security & Encryption Standards
      </h2>
      <p className="mt-4">
        We implement enterprise-grade technical and organizational safeguards to prevent
        unauthorized access, accidental loss, or unlawful processing:
      </p>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="p-4 rounded-[10px] bg-[#0A1322] border border-white/[0.08]">
          <h4 className="text-white font-medium text-[14px]">Encryption in Transit</h4>
          <p className="mt-1 text-[13px] text-[#8A99AD] leading-[20px]">
            All communications are secured using industry-standard TLS 1.3 cryptographic protocols.
          </p>
        </div>
        <div className="p-4 rounded-[10px] bg-[#0A1322] border border-white/[0.08]">
          <h4 className="text-white font-medium text-[14px]">Encryption at Rest</h4>
          <p className="mt-1 text-[13px] text-[#8A99AD] leading-[20px]">
            Stored assets and records utilize AES-256 standard encryption on secured infrastructure.
          </p>
        </div>
      </div>
    </section>

    {/* Section 6 */}
    <section id="priv-sharing" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        6. Data Sharing & Third Parties
      </h2>
      <p className="mt-4">
        Simpel does not sell, lease, or monetize personal information. Data is shared exclusively
        with vetted infrastructure vendors (e.g., secure cloud hosting, DNS routing, email delivery)
        who are contractually bound to strict confidentiality and security benchmarks.
      </p>
    </section>

    {/* Section 7 */}
    <section id="priv-rights" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        7. Your Rights & Choices
      </h2>
      <p className="mt-4">
        Depending on your jurisdiction, you may have the right to request access to, correction
        of, or deletion of your personal records. You may also opt out of promotional email
        communications at any time by following the unsubscribe link in our messages.
      </p>
    </section>

    {/* Section 8 */}
    <section id="priv-contact" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        8. Contact & Legal Requests
      </h2>
      <p className="mt-4">
        For inquiries regarding this policy or to submit a formal data privacy request, please contact
        our legal team at:
      </p>
      <div className="mt-3 p-4 rounded-[10px] bg-[#0A1322] border border-white/[0.08] text-[14px] text-white">
        <p className="font-semibold">Simpel Technologies Legal & Compliance</p>
        <p className="text-[#8A99AD] mt-1">
          Email:{" "}
          <a href="mailto:contact@simpel.ai" className="text-white hover:underline font-medium">
            contact@simpel.ai
          </a>
        </p>
      </div>
    </section>
  </div>
);

/* --- 2. TERMS OF USE DOCUMENT --- */
const TermsOfUseContent = () => (
  <div className="flex flex-col space-y-12 text-[#94A3B8] text-[15px] leading-[1.8]">
    <div className="rounded-[12px] bg-[#0A1426] border border-white/10 p-5 text-[14px] text-[#CBD5E1]">
      <p className="leading-[22px]">
        These Terms of Use govern access to Simpel's public websites and demonstration platforms.
        Commercial engagements are governed by formal Master Services Agreements (MSAs).
      </p>
    </div>

    <section id="terms-acceptance" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        1. Acceptance of Terms
      </h2>
      <p className="mt-4">
        By accessing or browsing this website, you acknowledge that you have read, understood, and
        agree to be bound by these Terms of Use and all applicable laws and regulations. If you do
        not agree with these terms, please discontinue using the website.
      </p>
    </section>

    <section id="terms-ip" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        2. Intellectual Property
      </h2>
      <p className="mt-4">
        All materials displayed on this site—including visual designs, brand marks, diagrams,
        whitepapers, software demonstrations, architectural frameworks, and proprietary methodology
        (such as Simpel Loop and Factory 360)—are the intellectual property of Simpel Technologies
        and are protected by copyright and intellectual property laws.
      </p>
    </section>

    <section id="terms-use" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        3. Permitted & Restricted Use
      </h2>
      <p className="mt-4">You agree to access and use our website strictly for lawful purposes. You shall not:</p>
      <ul className="mt-3 space-y-2 list-disc pl-5">
        <li>Attempt to circumvent security controls or probe network vulnerabilities.</li>
        <li>Deploy automated scrapers, crawlers, or harvesting tools without explicit authorization.</li>
        <li>Decompile, disassemble, or reverse-engineer any interactive software or simulation components.</li>
        <li>Impersonate any individual, representative, or affiliate of Simpel Technologies.</li>
      </ul>
    </section>

    <section id="terms-ai" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        4. AI Output & Advisory Scope
      </h2>
      <p className="mt-4">
        Conversations with our AI agent (Simmi) and interactive demonstrations on this website
        are provided for general information and preliminary scoping only. They do not constitute
        binding commercial guarantees, architectural blueprints, or warranties. Binding deliverables
        are defined exclusively in signed Statements of Work (SOWs).
      </p>
    </section>

    <section id="terms-liability" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        5. Limitation of Liability
      </h2>
      <p className="mt-4">
        To the fullest extent permitted by law, Simpel Technologies shall not be liable for any
        direct, indirect, incidental, consequential, or punitive damages resulting from your use of
        or inability to access this website, its demonstration tools, or downloaded informational reports.
      </p>
    </section>

    <section id="terms-governing" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        6. Governing Law & Jurisdiction
      </h2>
      <p className="mt-4">
        These Terms shall be governed by and interpreted in accordance with applicable laws,
        without giving effect to choice of law principles. Any legal proceeding arising out of
        these terms shall be submitted to competent courts of jurisdiction.
      </p>
    </section>
  </div>
);

/* --- 3. COOKIE POLICY DOCUMENT --- */
const CookiePolicyContent = () => (
  <div className="flex flex-col space-y-12 text-[#94A3B8] text-[15px] leading-[1.8]">
    <div className="rounded-[12px] bg-[#0A1426] border border-white/10 p-5 text-[14px] text-[#CBD5E1]">
      <p className="leading-[22px]">
        Simpel respects digital minimalism. We do not use intrusive third-party cross-site
        tracking networks. Cookies are limited to essential functionality and basic performance telemetry.
      </p>
    </div>

    <section id="cookie-intro" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        1. What Are Cookies?
      </h2>
      <p className="mt-4">
        Cookies are small text files placed on your device by websites you visit. They are widely
        used to ensure websites function efficiently, preserve navigation states, and provide basic
        operational analytics to website operators.
      </p>
    </section>

    <section id="cookie-types" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        2. Categories of Cookies Used
      </h2>
      <div className="mt-4 space-y-4">
        <div className="p-4 rounded-[10px] bg-[#0A1322] border border-white/[0.08]">
          <h3 className="text-white font-medium text-[15px]">Essential & Session Cookies</h3>
          <p className="mt-1 text-[13.5px] text-[#8A99AD] leading-[22px]">
            Required for secure navigation, preserving active tab selections, and managing
            conversational AI streaming state during an active session. These cannot be disabled
            without impacting basic website operations.
          </p>
        </div>
        <div className="p-4 rounded-[10px] bg-[#0A1322] border border-white/[0.08]">
          <h3 className="text-white font-medium text-[15px]">Performance & Latency Monitoring</h3>
          <p className="mt-1 text-[13.5px] text-[#8A99AD] leading-[22px]">
            Anonymous, aggregated metrics used to monitor load times, page render stability,
            and identify broken endpoints across diverse global network conditions.
          </p>
        </div>
      </div>
    </section>

    <section id="cookie-no-ads" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        3. No Third-Party Ad Trackers
      </h2>
      <p className="mt-4">
        Simpel does not host third-party advertising networks, affiliate trackers, or surveillance
        monetization pixels. Your browsing behavior on our domain is never packaged, sold, or shared
        with commercial data brokers.
      </p>
    </section>

    <section id="cookie-manage" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        4. Managing Cookie Preferences
      </h2>
      <p className="mt-4">
        You can configure your browser to block or alert you about cookies. Most modern browsers
        (Chrome, Edge, Safari, Firefox) provide straightforward privacy settings under their
        Options or Preferences menu. Note that blocking all cookies may affect video playback
        and dynamic interactive features.
      </p>
    </section>

    <section id="cookie-updates" className="scroll-mt-28">
      <h2 className="text-[21px] sm:text-[23px] font-bold text-white tracking-tight pb-3 border-b border-white/[0.08]">
        5. Policy Modifications
      </h2>
      <p className="mt-4">
        We periodically review this policy to reflect platform updates and legal standards.
        Any adjustments will be reflected immediately on this page with an updated revision date.
      </p>
    </section>
  </div>
);

export default LegalPage;
